import React, { useCallback, useEffect, useState } from 'react';

import { Box, Slider, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import { getCroppedImg } from '@app/helpers/canvasUtils';
import PropTypes from 'prop-types';
import { useTranslation } from '@app/config/i18n';
import CustomDialog from '@app/components/molecules/CustomDialog';
import Cropper from 'react-easy-crop';
import RoundedButton from '@app/components/atoms/RoundedButton';
import ImgDialog from './imgDialog';

export const styles = (theme) => ({
  cropContainer: {
    position: 'relative',
    width: '100%',
    height: 200,
    background: '#333',
    [theme.breakpoints.up('sm')]: {
      height: 400,
    },
  },
  cropButton: {
    flexShrink: 0,
    marginLeft: 16,
  },
  controls: {
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  sliderContainer: {
    display: 'flex',
    flex: '1',
    alignItems: 'center',
  },
  sliderLabel: {
    [theme.breakpoints.down('xs')]: {
      minWidth: 65,
    },
  },
  slider: {
    padding: '22px 0px',
    marginLeft: 16,
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      alignItems: 'center',
      margin: '0 16px',
    },
  },
});

const ImgEditor = ({ imgEditor, setImgEditor, classes }) => {
  const { t } = useTranslation(['common', 'account']);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);

  useEffect(() => {
    if (croppedImage) {
      setImgEditor((prevState) => ({
        ...prevState,
        fileUrl: URL.createObjectURL(croppedImage),
      }));
    }
  }, [croppedImage]);

  const onCropComplete = useCallback((croppedArea, cropedPixels) => {
    setCroppedAreaPixels(cropedPixels);
  }, []);

  const showCroppedImage = useCallback(async () => {
    try {
      const cropImage = await getCroppedImg(imgEditor.file, croppedAreaPixels, rotation);
      setCroppedImage(cropImage);
    } catch (e) {
      console.error(e);
    }
  }, [imgEditor.file, croppedAreaPixels, rotation]);

  const onClose = useCallback(() => {
    setCroppedImage(null);
    setImgEditor((prevState) => ({
      ...prevState,
      show: false,
    }));
  }, []);

  const handlePreview = () => {
    showCroppedImage();
  };
  const handleClose = () => {
    setImgEditor((prevState) => ({
      ...prevState,
      show: false,
    }));
  };
  const saveImage = () => {
    setImgEditor((prevState) => ({
      ...prevState,
      show: false,
      finalImage: croppedImage,
    }));
  };

  return (
    <CustomDialog
      open={imgEditor.show}
      onClose={handleClose}
      title={t('account:edit_img')}
    >
      <Box sx={{ width: 600 }}>
        {imgEditor.file ? (
          <>
            <div className={classes.cropContainer}>
              <Cropper
                image={imgEditor.file}
                crop={crop}
                rotation={rotation}
                zoom={zoom}
                aspect={4 / 3}
                onCropChange={setCrop}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>
            <div className={classes.controls}>
              <div className={classes.sliderContainer}>
                <Typography variant="overline" classes={{ root: classes.sliderLabel }}>
                  {t('zoom')}
                </Typography>
                <Slider
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  aria-labelledby="Zoom"
                  classes={{ root: classes.slider }}
                  onChange={(e, val) => setZoom(val)}
                />
              </div>
              <div className={classes.sliderContainer}>
                <Typography variant="overline" classes={{ root: classes.sliderLabel }}>
                  {t('account:rotation')}
                </Typography>
                <Slider
                  value={rotation}
                  min={0}
                  max={360}
                  step={1}
                  aria-labelledby="Rotation"
                  classes={{ root: classes.slider }}
                  onChange={(e, val) => setRotation(val)}
                />
              </div>
              <RoundedButton
                onClick={handleClose}
                variant="outlined"
                classes={{ root: classes.cropButton }}
              >
                {t('common:cancel')}
              </RoundedButton>

              <RoundedButton
                onClick={handlePreview}
                variant="contained"
                color="primary"
                classes={{ root: classes.cropButton }}
              >
                {t('account:prev')}
              </RoundedButton>
            </div>
            <ImgDialog img={imgEditor.fileUrl} saveImage={saveImage} onClose={onClose} />
          </>
        ) : null}
      </Box>
    </CustomDialog>
  );
};

ImgEditor.propTypes = {
  setImgEditor: PropTypes.func.isRequired,
  imgEditor: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

ImgEditor.defaultProps = {};
const StyledImgEditor = withStyles(styles)(ImgEditor);

export default StyledImgEditor;
